import { useCallback, useEffect, useMemo, useRef } from "react";
import { useApolloClient } from "@apollo/client/react";
import {
  type NotificationRow,
  NotificationsViewDataDocument,
  type NotificationsViewDataQuery,
} from "#app/lib/data";
import { usePagedConnection } from "#app/lib/hooks/use-paged-connection";
import { inboxFilterWhere, type InboxFilter } from "./inbox-filters";
import { DEFAULT_PAGE_SIZE } from "#app/lib/constants/page-size";

interface UseInboxOptions {
  showCleared: boolean;
  /** Active inbox filter — a type bucket or the Unread read-state filter. */
  filter?: InboxFilter | null;
}

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

export function useInbox({ showCleared, filter }: UseInboxOptions) {
  const variables = useMemo(
    () => ({
      showCleared,
      where: filter ? inboxFilterWhere(filter) : undefined,
    }),
    [showCleared, filter],
  );

  // Warm the OTHER tab's first unfiltered page once per mount, so the first
  // All↔Cleared flip answers from the cache in the same frame instead of
  // holding a skeleton for one short network roundtrip. `cache-first` makes a
  // revisit free; the visible tab still refreshes itself (cache-and-network)
  // when it actually opens. Filtered variable sets stay cold on purpose —
  // prefetching every filter combination would be four speculative queries.
  const client = useApolloClient();
  const prefetchedRef = useRef(false);
  useEffect(() => {
    if (prefetchedRef.current) return;
    prefetchedRef.current = true;
    void client
      .query({
        query: NotificationsViewDataDocument,
        variables: { showCleared: !showCleared, skip: 0, limit: PAGE_SIZE },
        fetchPolicy: "cache-first",
      })
      .catch(() => {
        // Purely speculative — the tab loads normally (skeleton) if it failed.
      });
  }, [client, showCleared]);

  // Capture unreadCount from the watched payload — only honor it on the
  // non-cleared tab so the badge always reflects the "All" count.
  const stableUnreadCount = useRef(0);
  const onData = useCallback(
    (data: NotificationsViewDataQuery | undefined) => {
      const next = data?.notifications?.unreadCount ?? 0;
      if (!showCleared) stableUnreadCount.current = next;
    },
    [showCleared],
  );

  const {
    items,
    total,
    hasMore,
    loading,
    loadingMore,
    fetchPage,
    refetch,
  } = usePagedConnection<NotificationRow, NotificationsViewDataQuery>({
    document: NotificationsViewDataDocument,
    variables,
    pageSize: PAGE_SIZE,
    getConnection: (d) => d.notifications,
    onData,
  });

  return {
    items,
    total,
    loading,
    loadingMore,
    hasMore,
    fetchPage,
    error: undefined,
    refetch,
    unreadCount: stableUnreadCount.current,
  };
}
